/**
 * A-10 カート（D-80／統合カート＝D-83・FR-127／**M1.9b＝シート化・D-101**）。
 *
 * ★**M1.9b（2026-08-10 森井氏決定）＝カートは画面でなく、どこからでも下から出るシート**
 *   （Design order-app.js の k:"cart" と同じ）。開閉は `cartUi.tsx`、この
 *   ファイルは**シートの中身**（`CartSheetBody`）と、カートの計算（`useCartGroups`）を持つ。
 * ★**内部は卸ごとに独立**（S12 仕様）＝見せ方だけが1つのカート（D-83）。
 * ★**localStorage に置かない**＝メモリ保持（単価の鮮度・cart.tsx）。
 * ★**金額の計算をここに置く**＝小計・配送料・税・合計は `useCartGroups`＋`taxOf` が唯一の実装。
 * ★**税（D-101・森井氏決定②）**＝小計（税抜）／消費税（10%）／合計（税込）の3行で出す。
 *   税率はここの `TAX_RATE` が唯一の定数（**表示のみ**＝注文・請求のデータは従来どおり税抜。
 *   請求書は各卸から届く＝正式な税額はそちらが正）。
 * ★配送料（FR-131・D-90）＝`mode='separate'` の卸だけ行を出す（「込み」の卸には何も出さない）。
 */

import {
  deliveryFeeFor,
  type ConnectedTenant,
  type DeliveryFee,
  type PartnerBin,
  type partnerApi,
} from "@kenzai/api-client";
import { Badge, ErrorNote, Money } from "@kenzai/ui";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCart, type CartLine } from "../cart";
import { useCartUi } from "../cartUi";
import { Sheet } from "../Sheet";
import { Input, Stepper } from "./formKit";

type Client = ReturnType<typeof partnerApi>;

/** 消費税率（**表示のみ**・D-101）。複数税率や請求上の税計算は M3 の請求で扱う（D-90⑩）。 */
export const TAX_RATE = 0.1;

/** 税抜合計 → 消費税額（円・四捨五入）。表示の唯一の実装＝画面ごとに違う税額を出さない。 */
export function taxOf(subtotal: number): number {
  return Math.round(subtotal * TAX_RATE);
}

/** 1卸ぶんのカート（画面のグループカード＝チェックアウトのグループと 1:1）。 */
export type CartGroup = {
  tenant: ConnectedTenant;
  lines: CartLine[];
  /** 商品計。**1行でも単価未定なら null**（FR-82＝未確定を0として足さない）。 */
  itemsTotal: number | null;
  /** 配送料（円）。**null＝この卸は「商品価格に込み」＝行を出さない**（D-90）。 */
  fee: number | null;
  feeSetting: DeliveryFee | undefined;
  bins: PartnerBin[] | undefined;
};

/**
 * カートのグループを組み立てる（A-10 と A-06 で**同じ計算**を使う＝カートと最終確認で
 * 金額の見え方が変わらない）。並びは**接続順**（カタログの卸の並びと揃える）。
 */
export function useCartGroups(client: Client, connections: ConnectedTenant[]) {
  const cart = useCart();
  const active = connections.filter((c) => cart.lines(c.tenant_id).length > 0);

  const feeQueries = useQueries({
    queries: active.map((c) => ({
      queryKey: ["delivery-fee", c.tenant_id],
      queryFn: () => client.deliveryFee(c.tenant_id),
      // 停止中の卸は 403 になる＝引きに行かない（画面はそのグループを「送れない」と出す）
      enabled: c.tenant_status !== "suspended",
    })),
  });
  const binQueries = useQueries({
    queries: active.map((c) => ({
      queryKey: ["bins", c.tenant_id],
      queryFn: () => client.bins(c.tenant_id),
      enabled: c.tenant_status !== "suspended",
    })),
  });

  const groups: CartGroup[] = active.map((tenant, i) => {
    const lines = cart.lines(tenant.tenant_id);
    const itemsTotal = lines.some((l) => l.unit_price === null)
      ? null
      : lines.reduce((sum, l) => sum + Number(l.unit_price) * l.qty, 0);
    const feeSetting = feeQueries[i]?.data;
    return {
      tenant,
      lines,
      itemsTotal,
      // 商品計が未確定なら配送料も出さない（無料判定が閾値に依存するため）
      fee: itemsTotal === null ? null : deliveryFeeFor(feeSetting, itemsTotal),
      feeSetting,
      bins: binQueries[i]?.data,
    };
  });

  // 停止中の卸は**送れない**＝合計にも入れない（合計と請求が食い違う数字を出さない）。
  const payable = groups.filter((g) => g.tenant.tenant_status !== "suspended");
  const grandTotal =
    payable.length === 0 || payable.some((g) => g.itemsTotal === null)
      ? null
      : payable.reduce((sum, g) => sum + (g.itemsTotal ?? 0) + (g.fee ?? 0), 0);

  return { groups, payable, grandTotal };
}

/** 店（卸）アイコン＝Design の store。 */
function StoreIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 4h16l1 5a3 3 0 0 1-3 3 3 3 0 0 1-3-3 3 3 0 0 1-6 0 3 3 0 0 1-3 3 3 3 0 0 1-3-3z" />
      <path d="M5 12v8h14v-8" />
      <path d="M9 20v-5h6v5" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3 22 20H2z" />
      <path d="M12 9.5v5M12 17.4h.01" />
    </svg>
  );
}

/**
 * カートシートの中身（Design の sheetCart と 1:1）。
 *
 * data-testid は従来の /cart 画面のものを**そのまま**引き継ぐ（cart / cart-group-* /
 * cart-line-* / cart-grand-total / to-checkout / empty-go-catalog …）＝E2E の指し先は不変。
 */
export function CartSheetBody({ client, connections }: { client: Client; connections: ConnectedTenant[] }) {
  const nav = useNavigate();
  const cart = useCart();
  const ui = useCartUi();
  const { groups, payable, grandTotal } = useCartGroups(client, connections);

  if (groups.length === 0) {
    return (
      <div data-testid="cart">
        <div className="sh-title">カート</div>
        <p className="oa-lead">カートはまだ空です。</p>
        {/* Design の steps3＝はじめての人への3手順（空のカートは説明の場所） */}
        <div className="steps3">
          <div>
            <span className="stp">1</span>
            <span>「注文する」で商品をタップ</span>
          </div>
          <div>
            <span className="stp">2</span>
            <span>数量を選んで「カートに追加」</span>
          </div>
          <div>
            <span className="stp">3</span>
            <span>レジで お届け先を選んで発注</span>
          </div>
        </div>
        <button
          type="button"
          data-testid="empty-go-catalog"
          className="kz-btn kz-btn--accent kz-pressable"
          style={{ width: "100%", marginTop: "var(--kz-space-3)" }}
          onClick={() => {
            ui.close();
            nav("/catalog");
          }}
        >
          商品を見る
        </button>
      </div>
    );
  }

  const count = groups.reduce((n, g) => n + g.lines.length, 0);
  const tax = grandTotal === null ? null : taxOf(grandTotal);

  return (
    <div data-testid="cart">
      <div className="sh-title">
        カート<small>{count}品目</small>
      </div>

      {groups.map((g) => (
        <div key={g.tenant.tenant_id} className="cg" data-testid={`cart-group-${g.tenant.tenant_id}`}>
          <div className="cg-h">
            <StoreIcon />
            <b>{g.tenant.tenant_name}</b>
            {g.tenant.tenant_status === "suspended" && <Badge tone="danger">利用できません</Badge>}
            <CutoffSmall group={g} />
          </div>
          {g.tenant.tenant_status === "suspended" && (
            <p
              data-testid={`cart-suspended-${g.tenant.tenant_id}`}
              style={{
                margin: 0,
                padding: "var(--kz-space-2) var(--kz-space-3)",
                fontSize: "var(--kz-font-size-sm)",
                color: "var(--kz-status-danger)",
              }}
            >
              この卸へのご注文は、いまお送りできません。カートの中身はそのまま残ります。
            </p>
          )}

          {g.lines.map((l) => (
            <div key={l.product_id} className="ci" data-testid={`cart-line-${l.product_id}`}>
              <span className="ci-n">
                <b>{l.name}</b>
                {l.unit_price !== null && (
                  <small className="kz-num">
                    ¥{Number(l.unit_price).toLocaleString("ja-JP")}/{l.unit}
                  </small>
                )}
              </span>
              <Stepper
                small
                value={l.qty}
                step={1}
                unit={l.unit}
                label={l.name}
                onChange={(v) => cart.setQty(g.tenant.tenant_id, l.product_id, v)}
              />
              <span className="ci-amt kz-num">
                <Money value={l.unit_price === null ? null : String(Number(l.unit_price) * l.qty)} />
              </span>
              <button
                type="button"
                className="ci-del kz-pressable"
                onClick={() => cart.remove(g.tenant.tenant_id, l.product_id)}
                aria-label={`${l.name} を削除`}
              >
                <TrashIcon />
              </button>
            </div>
          ))}

          {/* 配送料の内訳（FR-131・D-90）＝Design に無いが**実装済みの約束**＝グループ末尾に維持 */}
          <GroupTotals group={g} />
        </div>
      ))}

      {/* 全体合計（D-101・税は表示のみ）。`cart-grand-total` は従来どおり**税抜の合計**を指す */}
      <div className="ck-totals">
        <div className="ct-line">
          <span>小計（税抜）</span>
          <span data-testid="cart-grand-total" className="kz-num">
            <Money value={grandTotal === null ? null : String(grandTotal)} />
          </span>
        </div>
        <div className="ct-line">
          <span>消費税（10%）</span>
          <span data-testid="cart-tax" className="kz-num">
            <Money value={tax === null ? null : String(tax)} />
          </span>
        </div>
        <div className="ct-total">
          <span>合計（税込）</span>
          <span data-testid="cart-total-incl" className="kz-num">
            <Money value={grandTotal === null || tax === null ? null : String(grandTotal + tax)} />
          </span>
        </div>
      </div>

      {/* 複数卸の分割予告（Design cg-note）＝確定時に何が起きるかを先に言う（CS-/JU- と整合） */}
      {payable.length > 1 && (
        <div className="cg-note" data-testid="cart-split-note">
          <WarnIcon />
          <span>発注先が{payable.length}社あるため、確定時に{payable.length}件の注文に分かれます。</span>
        </div>
      )}

      <button
        type="button"
        data-testid="to-checkout"
        disabled={payable.length === 0}
        onClick={() => ui.openCheckout()}
        className="kz-btn kz-btn--accent kz-pressable"
        style={{
          width: "100%",
          minHeight: 52,
          marginTop: "var(--kz-space-3)",
          fontSize: "var(--kz-font-size-md)",
          cursor: payable.length === 0 ? "not-allowed" : "pointer",
        }}
      >
        レジに進む
      </button>

      {/* いつもの組み合わせを次回ワンタップにする（FR-132・D-100③＝保存の入口はここ） */}
      <SaveSetButton client={client} groups={payable} />
    </div>
  );
}

/** グループ見出しの右肩に出す締め表示（Design cg-h の small）。 */
function CutoffSmall({ group }: { group: CartGroup }) {
  if (group.tenant.tenant_status === "suspended") return null;
  const bins = group.bins;
  if (!bins || bins.length === 0) return null;
  const openToday = bins.find((b) => b.available_today);
  return (
    <small data-testid={`cart-cutoff-${group.tenant.tenant_id}`}>
      {openToday ? `本日 ${openToday.cutoff_time} まで受付（${openToday.name}）` : "本日の受付は終了"}
    </small>
  );
}

/** グループの小計・配送料（FR-131・D-90）。**「込み」の卸には何も出さない**。 */
export function GroupTotals({ group }: { group: CartGroup }) {
  const { itemsTotal, fee, feeSetting } = group;
  const threshold = feeSetting?.free_threshold ?? null;
  const shortfall =
    feeSetting?.mode === "separate" && threshold !== null && itemsTotal !== null && fee !== 0
      ? Number(threshold) - itemsTotal
      : null;

  return (
    <div style={{ padding: "var(--kz-space-2) var(--kz-space-3) var(--kz-space-3)" }}>
      <div className="ct-line">
        <span>商品計</span>
        <span className="kz-num">
          <Money value={itemsTotal === null ? null : String(itemsTotal)} />
        </span>
      </div>
      {fee !== null && (
        <div className="ct-line">
          <span>
            配送料
            {fee === 0 && threshold !== null && `（${Number(threshold).toLocaleString("ja-JP")}円以上のご注文）`}
          </span>
          <span data-testid={`cart-fee-${group.tenant.tenant_id}`} className="kz-num">
            <Money value={String(fee)} />
          </span>
        </div>
      )}
      {shortfall !== null && shortfall > 0 && (
        <p
          data-testid={`cart-free-hint-${group.tenant.tenant_id}`}
          style={{ margin: 0, fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-primary)" }}
        >
          あと <span className="kz-num">{shortfall.toLocaleString("ja-JP")}</span> 円のご注文で配送料無料
        </p>
      )}
      {feeSetting?.note && (
        <p style={{ margin: 0, fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
          {feeSetting.note}
        </p>
      )}
      <div className="ct-total" style={{ fontSize: "var(--kz-font-size-sm)" }}>
        <span>お取引先の小計</span>
        <span data-testid={`cart-subtotal-${group.tenant.tenant_id}`} className="kz-num">
          <Money value={itemsTotal === null ? null : String(itemsTotal + (fee ?? 0))} />
        </span>
      </div>
    </div>
  );
}

/**
 * 「このカートをセットとして保存」（FR-132・D-100③）。
 * ★保存するのは tenant_id・product_id・数量だけ（単価を保存しない＝D-100①）。
 */
export function SaveSetButton({
  client,
  groups,
  idPrefix = "save-set",
}: {
  client: Client;
  groups: CartGroup[];
  /**
   * testid の接頭辞。既定＝`save-set`（カートシート）。
   * ★**PC の右カートは `rail-save-set` を渡す**＝PC ではカタログの右レールとカートシートが
   *   同時に DOM に居られる（レールは常設・シートは重なる）ので、同じ testid が2つになると
   *   Playwright の strict mode で「どちらを押すか決められない」と落ちる（実測）。
   *   E2E が押しているのは**シート側の `save-set`**＝そちらの名前は動かさない。
   */
  idPrefix?: string;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [savedName, setSavedName] = useState<string | null>(null);

  const items = groups.flatMap((g) =>
    g.lines.map((l) => ({ tenant_id: g.tenant.tenant_id, product_id: l.product_id, qty: String(l.qty) })),
  );

  const save = useMutation({
    mutationFn: () => client.createSavedSet(name.trim(), items),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["saved-sets"] });
      setOpen(false);
      setSavedName(created.name);
      setName("");
    },
  });

  if (items.length === 0) return null;

  return (
    <>
      <button
        type="button"
        data-testid={idPrefix}
        onClick={() => {
          setSavedName(null);
          setOpen(true);
        }}
        className="kz-btn kz-btn--flat kz-pressable"
        style={{ width: "100%", minHeight: 44, marginTop: "var(--kz-space-2)" }}
      >
        このカートをセットとして保存
      </button>
      {savedName !== null && (
        <p
          data-testid={`${idPrefix}-done`}
          role="status"
          style={{
            margin: "var(--kz-space-2) 0 0",
            fontSize: "var(--kz-font-size-sm)",
            color: "var(--kz-color-primary)",
          }}
        >
          「{savedName}」を保存しました。「保存済み」タブからいつでもまとめてカートに入れられます。
        </p>
      )}

      {open && (
        <Sheet label="セットとして保存" onClose={() => setOpen(false)} testId={`${idPrefix}-sheet`}>
          <div style={{ display: "grid", gap: "var(--kz-space-3)" }}>
            <div className="sh-title">セットとして保存</div>
            <p className="oa-lead" style={{ margin: 0 }}>
              いまのカートの{items.length}
              品目を、「保存済み」からいつでもまとめてカートに入れられるようにします（金額は保存せず、
              使うときのお取引単価で計算します）。
            </p>
            <Input
              label="セットの名前（必須）"
              value={name}
              onChange={setName}
              maxLength={100}
              placeholder="例）内装下地 いつものセット"
              data-testid={`${idPrefix}-name`}
            />
            <ErrorNote error={save.error} />
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={name.trim() === "" || save.isPending}
              data-testid={`${idPrefix}-submit`}
              className="kz-btn kz-btn--accent kz-pressable"
              style={{ minHeight: 48 }}
            >
              {save.isPending ? "保存中…" : "保存する"}
            </button>
          </div>
        </Sheet>
      )}
    </>
  );
}

/**
 * PC（≥1024px）カタログの右固定カート（D-98⑤・FR-123 改）＝**買いながら見えるカート**。
 * ★確定はここでさせない（「レジに進む」→ レジシート）＝氏名ゲート・冪等キー・卸グループごとの
 *   入力はチェックアウトの持ち場（D-87／D-84）。
 */
export function CartRail({ client, connections }: { client: Client; connections: ConnectedTenant[] }) {
  const cart = useCart();
  const ui = useCartUi();
  const { groups, payable, grandTotal } = useCartGroups(client, connections);
  const count = groups.reduce((n, g) => n + g.lines.length, 0);
  const tax = grandTotal === null ? null : taxOf(grandTotal);

  return (
    <aside className="kz-cart-rail" aria-label="カート" data-testid="cart-rail">
      <div
        className="kp-cart"
        style={{
          background: "var(--kz-color-surface)",
          borderRadius: "var(--kz-radius-md)",
          boxShadow: "var(--kz-shadow-sm)",
          padding: "16px 18px",
        }}
      >
        {/* 見出し＝カート印＋件数ピル＋右肩に「全卸共通」（Design `.kp-cart-h`）。
            「全卸共通」は**1つのカートに全部入る**ことの説明＝卸ごとにカートがあると
            思っている人に、下のグループ枠を読む前に前提を渡す。 */}
        <h2 className="kp-cart-h">
          <StoreCartIcon />
          カート
          {count > 0 && <span className="kp-cart-cnt kz-num">{count}</span>}
          <small>全卸共通</small>
        </h2>

        {count === 0 ? (
          <p className="kp-cart-empty" style={{ margin: 0 }}>
            左の商品カードから追加してください。
          </p>
        ) : (
          <div style={{ display: "grid", gap: 0 }}>
            {/* ★卸ごとの枠＝カートシートと**同じ `.cg`/`.ci`**（Design の `kp-cg`）。
                同じ意味に同じ形を使う＝レール用の部品を別に作らない。
                testid はレール専用（`rail-`）＝シートと同時に DOM に居られるため。 */}
            {groups.map((g) => (
              <div key={g.tenant.tenant_id} className="cg" data-testid={`rail-group-${g.tenant.tenant_id}`}>
                <div className="cg-h">
                  <StoreIcon />
                  <b style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {g.tenant.tenant_name}
                  </b>
                  {g.tenant.tenant_status === "suspended" && <Badge tone="danger">利用できません</Badge>}
                  <RailCutoff group={g} />
                </div>

                {g.lines.map((l) => (
                  <div key={l.product_id} className="ci" data-testid={`rail-line-${l.product_id}`}>
                    <span className="ci-n">
                      <b>{l.name}</b>
                      {l.unit_price !== null && (
                        <small className="kz-num">
                          ¥{Number(l.unit_price).toLocaleString("ja-JP")}/{l.unit}
                        </small>
                      )}
                    </span>
                    {/* ★数量はレールでも直せる（Design）＝カタログを見ながら数を詰める場所。
                        計算は `useCartGroups` が唯一の実装なので、直した瞬間に下の合計も動く。 */}
                    <Stepper
                      small
                      value={l.qty}
                      step={1}
                      unit={l.unit}
                      label={l.name}
                      onChange={(v) => cart.setQty(g.tenant.tenant_id, l.product_id, v)}
                    />
                    <span className="ci-amt kz-num">
                      <Money value={l.unit_price === null ? null : String(Number(l.unit_price) * l.qty)} />
                    </span>
                    <button
                      type="button"
                      onClick={() => cart.remove(g.tenant.tenant_id, l.product_id)}
                      aria-label={`${l.name} を削除`}
                      className="ci-del kz-pressable"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                ))}

                {/* 配送料（FR-131・D-90）＝「込み」の卸には何も出さない。
                    `GroupTotals` は使わない＝あちらの testid（`cart-fee-*` ほか）は
                    カートシートのもので、レールと二重に出すと指し先が決まらなくなる。 */}
                {g.fee !== null && (
                  <div className="ct-line" style={{ padding: "6px 10px" }}>
                    <span>配送料</span>
                    <span data-testid={`rail-fee-${g.tenant.tenant_id}`} className="kz-num">
                      <Money value={String(g.fee)} />
                    </span>
                  </div>
                )}
              </div>
            ))}

            <div className="kp-cart-foot-totals">
              <div className="ct-line">
                <span>小計（税抜）</span>
                <span data-testid="rail-total" className="kz-num">
                  <Money value={grandTotal === null ? null : String(grandTotal)} />
                </span>
              </div>
              <div className="ct-line">
                <span>消費税（10%）</span>
                <span className="kz-num">
                  <Money value={tax === null ? null : String(tax)} />
                </span>
              </div>
              <div className="ct-total">
                <span>合計（税込）</span>
                <span className="kz-num">
                  <Money value={grandTotal === null || tax === null ? null : String(grandTotal + tax)} />
                </span>
              </div>
            </div>

            {/* 分割の予告＝Design の「発注を確定する（N件）」が持っていた情報を、確定させない
                レールでも渡す（確定は氏名ゲート・冪等キーがあるレジの持ち場＝D-84/D-87）。 */}
            {payable.length > 1 && (
              <div className="kp-split-note" data-testid="rail-split-note">
                <WarnIcon />
                <span>
                  発注先が{payable.length}社あるため、送信時に{payable.length}件の注文に分かれます。
                </span>
              </div>
            )}

            <button
              type="button"
              data-testid="rail-to-checkout"
              disabled={payable.length === 0}
              onClick={() => ui.openCheckout()}
              className="kz-btn kz-btn--accent kz-pressable"
              style={{ minHeight: 48, marginTop: "var(--kz-space-3)" }}
            >
              レジに進む
            </button>
            <SaveSetButton client={client} groups={payable} idPrefix="rail-save-set" />

            {/* ★レールに置けないもの（お届け先・便・希望日）が**どこにあるか**を言う。
                これらは卸グループごとに違う＝1つの選択欄に畳むと注文の作られ方が壊れる。 */}
            <p className="kp-cart-foot">
              お届け先・便・納品希望日は、「レジに進む」の先でお取引先ごとに選びます。
              <br />
              送信したご注文は「注文履歴」と現場の原価にすぐ反映されます。
            </p>
          </div>
        )}
      </div>
    </aside>
  );
}

/** レールのグループ見出しの右肩＝締めの予告（カートシートの `CutoffSmall` と同じ判定）。 */
function RailCutoff({ group }: { group: CartGroup }) {
  if (group.tenant.tenant_status === "suspended") return null;
  const bins = group.bins;
  if (!bins || bins.length === 0) return null;
  const openToday = bins.find((b) => b.available_today);
  return (
    // ★レールは 330px＝**卸名が読める幅を先に確保する**。カートシート（画面幅いっぱい）の
    //   「本日 10:00 まで受付（午前便）」をそのまま置くと、帯の大半を締めが占めて卸名が
    //   3文字で切れる（実測）。Design の帯と同じ短い言い方にする。
    <small data-testid={`rail-cutoff-${group.tenant.tenant_id}`}>
      {openToday ? `${openToday.cutoff_time.slice(0, 5)}締め・${openToday.name}` : "本日の受付は終了"}
    </small>
  );
}

/** レールの見出しのカート印（Design `.kp-cart-h svg`）。 */
function StoreCartIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <path d="M3 4h2.2l2.4 10.5h9.6L19 7H6.2" />
      <circle cx="9.5" cy="19" r="1.4" />
      <circle cx="17" cy="19" r="1.4" />
    </svg>
  );
}
